/**
 * FadeIn
 *
 * SHS Visual Experience System — Sprint 29.1A.
 *
 * Opacity 0 → 1 entrance for a section of content.
 *
 * Safety contract:
 *   - Never leaves content permanently invisible. The underlying CSS
 *     keyframe ends at `opacity: 1`, and if the animation class is not
 *     applied at all (reduced-motion, unmounted early, animation fails),
 *     the element's natural opacity is 1 — so content is ALWAYS readable.
 *   - Semantic HTML: renders whatever tag the caller specifies via `as`,
 *     default <div>. Does not wrap children in an inaccessible container.
 *   - No layout shift: the primitive occupies the same box before and
 *     after the animation.
 *   - Does not block interaction: no pointer-events changes, no overlay.
 *
 * Reduced-motion behavior:
 *   In `reduced` mode the animation class is not applied. Content renders
 *   immediately at its final visual state.
 *
 * Not applied to any existing SHS page in Sprint 29.1A.
 */

import React from 'react';
import { useMotionMode } from '../hooks/useMotionMode';

export const FadeIn = ({
  as: Tag = 'div',
  className = '',
  style,
  children,
  ...rest
}) => {
  const { isReduced } = useMotionMode();
  const animationClass = isReduced ? '' : 'motion-fade-in';
  const composed = [className, animationClass].filter(Boolean).join(' ');

  return (
    <Tag className={composed} style={style} {...rest}>
      {children}
    </Tag>
  );
};

export default FadeIn;
